import { config } from '../config';
import { authorizedFetch } from './auth';

/**
 * The MyParcel API key is stored server-side, keyed by Cognito subject, and is
 * never sent back to the browser. These two calls are all the UI can see: whether
 * a key exists, and a write that only succeeds if MyParcel accepts it.
 */

export type KeyStatus = {
  configured: boolean;
  lastModified: string | null;
};

export async function fetchKeyStatus(): Promise<KeyStatus> {
  const response = await authorizedFetch(`${config().apiBase}/settings/api-key/status`);

  if (!response.ok) {
    throw new Error(`Kon de status niet ophalen (HTTP ${response.status})`);
  }

  const body = (await response.json()) as { configured?: boolean; last_modified?: string | null };
  return {
    configured: body.configured === true,
    lastModified: body.last_modified ?? null,
  };
}

export class KeyRejectedError extends Error {}

/**
 * Stores the key after the backend has validated it against MyParcel. A rejected
 * key is a normal outcome, not a transport failure, so it gets its own error type
 * and carries the reason the backend gave.
 */
export async function saveApiKey(apiKey: string): Promise<void> {
  const response = await authorizedFetch(`${config().apiBase}/settings/api-key`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ api_key: apiKey }),
  });

  if (response.ok) {
    return;
  }

  let reason: string | undefined;
  try {
    reason = ((await response.json()) as { error?: string }).error;
  } catch {
    // Non-JSON body; fall back to the status code below.
  }

  if (response.status === 400) {
    throw new KeyRejectedError(reason ?? 'MyParcel heeft deze API key niet geaccepteerd.');
  }
  throw new Error(reason ?? `Opslaan is mislukt (HTTP ${response.status})`);
}
